"use client";

import { Suspense } from "react";

import {
  Alert,
  Button,
  Card,
  Center,
  Container,
  Input,
  PinInput,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { coreApi } from "@/lib/core-api";

type LoginStep = "credentials" | "twoFactor";

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<LoginStep>("credentials");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [challengeExpiresIn, setChallengeExpiresIn] = useState<number | null>(
    null,
  );

  const form = useForm({
    initialValues: {
      username: "",
      password: "",
      code: "",
    },
    validate: (values) => {
      if (step === "twoFactor") {
        return {
          code:
            values.code.trim().length === 6 &&
            /^[0-9]+$/.test(values.code.trim())
              ? null
              : "Le code doit contenir 6 chiffres",
        };
      }

      return {
        username:
          values.username.trim().length >= 3
            ? null
            : "Veuillez saisir votre adresse e-mail",
        password:
          values.password.trim().length >= 6
            ? null
            : "Le mot de passe doit contenir au moins 6 caracteres",
      };
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      if (step === "credentials") {
        const response = await coreApi.post("login", {
          json: {
            username: values.username,
            password: values.password,
          },
        });

        const body = (await response.json().catch(() => null)) as {
          error?: string;
          requiresTwoFactor?: boolean;
          challengeId?: string;
          expiresIn?: number;
        } | null;

        if (!response.ok) {
          throw new Error(
            body?.error ??
              "Impossible de vous connecter avec ces identifiants.",
          );
        }

        if (body?.requiresTwoFactor) {
          if (!body.challengeId) {
            throw new Error("Impossible de demarrer la verification.");
          }
          setChallengeId(body.challengeId);
          setChallengeExpiresIn(body.expiresIn ?? null);
          setStep("twoFactor");
          form.setFieldValue("code", "");
          return;
        }

        const redirectPath = searchParams.get("redirect");
        const safeRedirect =
          redirectPath && redirectPath.startsWith("/")
            ? redirectPath
            : "/dashboard";

        router.replace(safeRedirect);
        router.refresh();
        return;
      }

      if (!challengeId) {
        throw new Error(
          "Le code de verification a expire. Veuillez vous reconnecter.",
        );
      }

      const response = await coreApi.post("login/verify", {
        json: {
          challengeId,
          code: values.code,
        },
      });

      const body = (await response.json().catch(() => null)) as {
        error?: string;
      } | null;

      if (!response.ok) {
        throw new Error(
          body?.error ?? "Le code de verification est invalide ou expire.",
        );
      }

      const redirectPath = searchParams.get("redirect");
      const safeRedirect =
        redirectPath && redirectPath.startsWith("/")
          ? redirectPath
          : "/dashboard";

      router.replace(safeRedirect);
      router.refresh();
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de la connexion. Merci de reessayer.",
      );
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Center style={{ minHeight: "100vh" }}>
      <Container size="xs">
        <Card
          withBorder
          padding="xl"
          radius="lg"
          component="form"
          onSubmit={handleSubmit}
        >
          <Stack gap="lg" align="center">
            <Image
              src="/logo.jpeg"
              alt="Logo Flute Send"
              width={256}
              height={128}
              priority
              style={{ borderRadius: "24px" }}
            />
            <Stack gap={4} align="center">
              <Title order={2}>Bienvenue</Title>
              <Text c="dimmed" ta="center">
                Connectez-vous à Flute Send
              </Text>
            </Stack>

            <Stack gap="sm" w="100%">
              {submitError ? (
                <Alert color="red" title="Echec de la connexion">
                  {submitError}
                </Alert>
              ) : null}
              {step === "credentials" ? (
                <>
                  <TextInput
                    label="E-mail"
                    placeholder="vous@exemple.com"
                    type="email"
                    required
                    autoComplete="email"
                    {...form.getInputProps("username")}
                  />
                  <PasswordInput
                    label="Mot de passe"
                    placeholder="Votre mot de passe"
                    required
                    autoComplete="current-password"
                    {...form.getInputProps("password")}
                  />

                  <Button type="submit" loading={isSubmitting} fullWidth>
                    Se connecter
                  </Button>
                </>
              ) : (
                <>
                  <Alert color="blue" title="Verification en deux etapes">
                    Un code de verification a ete envoye par e-mail.
                    {challengeExpiresIn
                      ? ` Il expire dans ${Math.max(
                          1,
                          Math.round(challengeExpiresIn / 60),
                        )} minute${
                          Math.round(challengeExpiresIn / 60) > 1 ? "s" : ""
                        }.`
                      : null}
                  </Alert>
                  <Input.Wrapper
                    label="Code de verification"
                    required
                    error={form.errors.code}
                  >
                    <PinInput
                      placeholder="•"
                      length={6}
                      type="number"
                      inputMode="numeric"
                      oneTimeCode
                      value={form.values.code}
                      onChange={(value) => form.setFieldValue("code", value)}
                      onBlur={() => form.validateField("code")}
                    />
                  </Input.Wrapper>
                  <Button type="submit" loading={isSubmitting} fullWidth>
                    Verifier
                  </Button>
                  <Button
                    type="button"
                    variant="subtle"
                    onClick={() => {
                      setStep("credentials");
                      setChallengeId(null);
                      setChallengeExpiresIn(null);
                      setSubmitError(null);
                      form.setFieldValue("code", "");
                    }}
                  >
                    Retour
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Card>
      </Container>
    </Center>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  );
}
